import type { AbsenceDraft } from "../types";

const DAY_MS = 86_400_000;

function parse(value: string | null): Date | null {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00`);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(
  value: string | null,
  locale: string,
): string | null {
  const date = parse(value);

  return date
    ? new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date)
    : null;
}

/** The range as the list and the detail title both read it. */
export function formatRange(
  beginDate: string | null,
  endDate: string | null,
  locale: string,
): string {
  const begin = formatDate(beginDate, locale);
  const end = formatDate(endDate, locale);

  if (begin && end) {
    return begin === end ? begin : `${begin} → ${end}`;
  }

  return begin ?? end ?? "";
}

/** Calendar days spanned, both ends included, the way the back end counts. */
export function dayCount(
  beginDate: string | null,
  endDate: string | null,
): number {
  const begin = parse(beginDate);

  if (!begin) {
    return 0;
  }

  const end = parse(endDate);

  if (!end) {
    return 1;
  }

  const days = Math.round((end.getTime() - begin.getTime()) / DAY_MS) + 1;

  return days > 0 ? days : 0;
}

export function draftDayCount(draft: AbsenceDraft): number {
  return dayCount(draft.beginDate, draft.endDate);
}

export function yearOf(value: string | null): string {
  return value?.slice(0, 4) ?? "";
}

/** Two letters standing in for a photo, as the identity bar shows them. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (0 === words.length) {
    return "?";
  }

  // Names read as FAMILY Given Second here, so the tail is the personal part
  const tail = words.slice(-2);
  const letters = tail.map((word) => word[0] ?? "").join("");

  return letters.toUpperCase();
}
